export { VALID_USER_RELATIONS, isValidUserRelation } from '@explorins/pers-shared';
import { b as buildPaginationParams, n as normalizeToPaginated } from './chunks/pagination-utils-9vQ-Npkr.js';

/**
 * Platform-Agnostic User API Client
 *
 * Handles user operations using the PERS backend RESTful API.
 * Updated to use new /users endpoints with enhanced security and consistency.
 * Maintains framework UserApiService method compatibility.
 */
class UserApi {
    constructor(apiClient) {
        this.apiClient = apiClient;
        this.basePath = '/users';
    }
    // ==========================================
    // PUBLIC OPERATIONS
    // ==========================================
    /**
     * PUBLIC: Get all users public profiles with optional filtering
     * Uses new RESTful /users/public endpoint
     */
    async getAllUsersPublicProfiles(filter = null, options) {
        let url = `${this.basePath}/public`;
        const params = new URLSearchParams();
        if (filter) {
            // Same parameter pattern for compatibility
            params.set('filterKey', filter.key);
            params.set('filterValue', filter.value);
        }
        // Add pagination parameters
        if (options) {
            Object.entries(buildPaginationParams(options)).forEach(([key, value]) => {
                params.set(key, value);
            });
        }
        if (params.toString()) {
            url += `?${params.toString()}`;
        }
        const response = await this.apiClient.get(url);
        return normalizeToPaginated(response);
    }
    // ==========================================
    // AUTHENTICATED OPERATIONS
    // ==========================================
    /**
     * AUTH: Get current authenticated user
     * Uses new RESTful /users/me endpoint
     *
     * @param options - Query options. Use `include` to specify relations: 'status' (user status types), 'balances' (token balances)
     */
    async getRemoteUser(options) {
        let url = `${this.basePath}/me`;
        if (options?.include?.length) {
            url += `?include=${options.include.join(',')}`;
        }
        return this.apiClient.get(url);
    }
    /**
     * AUTH: Update current authenticated user
     * Uses new RESTful /users/me endpoint
     */
    async updateRemoteUser(updateRequest) {
        return this.apiClient.put(`${this.basePath}/me`, updateRequest);
    }
    // ==========================================
    // ADMIN OPERATIONS
    // ==========================================
    /**
     * BUSINESS/ADMIN: Create or update user account
     *
     * Creates a new user or updates an existing one.
     * - Business auth: Can create/update single users (requires canManageUsers permission)
     * - Admin auth: Can create/update single users or bulk operations
     *
     * @param userData - User data for creation/update
     * @returns Created or updated user
     */
    async createOrUpdateUser(userData) {
        return this.apiClient.post(this.basePath, userData);
    }
    /**
     * ADMIN: Bulk create or update users
     *
     * Creates or updates multiple users in a single operation.
     * Requires admin authentication - business auth cannot perform bulk operations.
     *
     * @param users - Array of user data for creation/update
     * @returns Array of created/updated users
     */
    async createOrUpdateUsers(users) {
        return this.apiClient.post(this.basePath, users);
    }
    /**
     * ADMIN: Get all remote users with query parameters
     * Uses new RESTful /users endpoint with role-based access
     * Note: Admin users get full data, non-admin users get public profiles only
     *
     * @param options - Pagination options (page, limit, sortBy, sortOrder)
     * @param search - Optional search query to filter users
     *
     * @example
     * ```typescript
     * // Get users sorted by email
     * const users = await sdk.users.getAllUsers({
     *   page: 1,
     *   limit: 10,
     *   sortBy: 'email',
     *   sortOrder: SortOrder.ASC
     * });
     *
     * // Search for users
     * const searchResults = await sdk.users.getAllUsers({ page: 1, limit: 10 }, 'john');
     * ```
     */
    async getAllRemoteUsers(options, search) {
        const params = buildPaginationParams(options);
        if (search) {
            params.set('search', search);
        }
        const url = `${this.basePath}?merge=soft&${params.toString()}`;
        const response = await this.apiClient.get(url);
        return normalizeToPaginated(response);
    }
    /**
     * ADMIN: Update user as admin
     * Uses new RESTful /users/{id} endpoint
     */
    async updateUserAsAdmin(id, userData) {
        return this.apiClient.put(`${this.basePath}/${id}`, userData);
    }
    /**
     * ADMIN: Set or toggle user active status
     * Uses new consistent /users/{id}/status endpoint
     * Enhanced: Follows RESTful status management pattern across all domains
     *
     * @param userId - User ID
     * @param isActive - Optional explicit status. If provided, sets to this value. If omitted, toggles current status.
     *
     * @example
     * ```typescript
     * // Explicit set
     * await sdk.users.setUserActiveStatus('user-123', true);   // Activate
     * await sdk.users.setUserActiveStatus('user-123', false);  // Deactivate
     *
     * // Toggle (current behavior)
     * await sdk.users.setUserActiveStatus('user-123');  // Flips current status
     * ```
     */
    async setUserActiveStatus(userId, isActive) {
        const body = isActive !== undefined ? { isActive } : {};
        return this.apiClient.put(`${this.basePath}/${userId}/status`, body);
    }
    /**
     * ADMIN: Get user by unique identifier
     * Uses new RESTful /users/{id} endpoint
     *
     * @param id - User unique identifier (id, email, externalId, accountAddress, etc.)
     * @param options - Query options. Use `include` to specify relations: 'status' (user status types), 'balances' (token balances)
     */
    async getUserByUniqueIdentifier(id, options) {
        let url = `${this.basePath}/${id}`;
        if (options?.include?.length) {
            url += `?include=${options.include.join(',')}`;
        }
        return this.apiClient.get(url);
    }
    /**
     * ADMIN: Delete user by identifier (soft delete)
     * Uses RESTful /users/{identifier} DELETE endpoint
     *
     * Soft deletes a user. The user data is retained for 30 days before GDPR anonymization.
     * Use restoreUser() to restore within the grace period.
     *
     * @param identifier - User unique identifier (id, email, externalId, accountAddress, etc.)
     * @returns Promise resolving to success status and message
     */
    async deleteUser(identifier) {
        return this.apiClient.delete(`${this.basePath}/${identifier}`);
    }
    /**
     * ADMIN: Restore deleted user by identifier
     * Uses RESTful /users/{identifier}/restore POST endpoint
     *
     * Restores a soft-deleted user within the 30-day grace period.
     * After GDPR anonymization (30 days), restoration is not possible.
     *
     * @param identifier - User unique identifier (id, email, externalId, accountAddress, etc.)
     * @returns Promise resolving to restored user data
     */
    async restoreUser(identifier) {
        return this.apiClient.post(`${this.basePath}/${identifier}/restore`);
    }
    // ==========================================
    // CSV EXPORT
    // ==========================================
    /**
     * ADMIN: Export users to CSV
     *
     * Returns a CSV file with user data for the tenant.
     * Supports date range filtering.
     *
     * @param options - Export options (dateFrom, dateTo in ISO format)
     * @returns CSV blob
     *
     * @example
     * ```typescript
     * // Export all users
     * const blob = await sdk.users.exportCSV();
     *
     * // Export users created in date range
     * const blob = await sdk.users.exportCSV({
     *   dateFrom: '2026-01-01',
     *   dateTo: '2026-05-31'
     * });
     * ```
     */
    async exportCSV(options) {
        const params = new URLSearchParams();
        if (options?.dateFrom)
            params.append('dateFrom', options.dateFrom);
        if (options?.dateTo)
            params.append('dateTo', options.dateTo);
        const queryString = params.toString();
        const url = `${this.basePath}/export/csv${queryString ? `?${queryString}` : ''}`;
        return this.apiClient.get(url, 'blob');
    }
}

/**
 * Platform-Agnostic User Service
 *
 * Contains user business logic and operations that work across platforms.
 * No framework dependencies - pure TypeScript business logic.
 * Matches framework UserApiService capabilities exactly.
 */
class UserService {
    constructor(userApi) {
        this.userApi = userApi;
    }
    // ==========================================
    // PUBLIC OPERATIONS
    // ==========================================
    /**
     * PUBLIC: Get all users public profiles with optional filtering
     * ✅ FIXED: Uses framework-compatible inline filter type
     */
    async getAllUsersPublicProfiles(filter = null, options) {
        return this.userApi.getAllUsersPublicProfiles(filter, options);
    }
    // ==========================================
    // AUTHENTICATED OPERATIONS
    // ==========================================
    /**
     * AUTH: Get current authenticated user
     *
     * @param options - Query options. Use `include` to specify relations: 'status' (user status types), 'balances' (token balances)
     */
    async getRemoteUser(options) {
        return this.userApi.getRemoteUser(options);
    }
    /**
     * AUTH: Update current authenticated user
     */
    async updateRemoteUser(updateRequest) {
        return this.userApi.updateRemoteUser(updateRequest);
    }
    // ==========================================
    // ADMIN OPERATIONS
    // ==========================================
    /**
     * BUSINESS/ADMIN: Create or update user account
     *
     * Creates a new user or updates an existing one.
     * - Business auth: Can create/update single users (requires canManageUsers permission)
     * - Admin auth: Can create/update single users or bulk operations
     *
     * @param userData - User data for creation/update
     * @returns Created or updated user
     */
    async createOrUpdateUser(userData) {
        return this.userApi.createOrUpdateUser(userData);
    }
    /**
     * ADMIN: Bulk create or update users
     *
     * Creates or updates multiple users in a single operation.
     * Requires admin authentication - business auth cannot perform bulk operations.
     *
     * @param users - Array of user data for creation/update
     * @returns Array of created/updated users
     */
    async createOrUpdateUsers(users) {
        return this.userApi.createOrUpdateUsers(users);
    }
    /**
     * ADMIN: Get all remote users
     * ✅ FIXED: Matches API method signature (no parameters needed)
     * @param options - Pagination options
     * @param search - Optional search query
     */
    async getAllRemoteUsers(options, search) {
        return this.userApi.getAllRemoteUsers(options, search);
    }
    /**
     * ADMIN: Update user as admin
     */
    async updateUserAsAdmin(id, userData) {
        return this.userApi.updateUserAsAdmin(id, userData);
    }
    /**
     * ADMIN: Get user by unique identifier
     *
     * @param id - User unique identifier (id, email, externalId, accountAddress, etc.)
     * @param options - Query options. Use `include` to specify relations: 'status' (user status types), 'balances' (token balances)
     */
    async getUserByUniqueIdentifier(id, options) {
        return this.userApi.getUserByUniqueIdentifier(id, options);
    }
    /**
     * ADMIN: Set or toggle user active status
     *
     * @param userId - User ID
     * @param isActive - Optional explicit status. If provided, sets to this value. If omitted, toggles current status.
     */
    async setUserActiveStatus(userId, isActive) {
        return this.userApi.setUserActiveStatus(userId, isActive);
    }
    /**
     * ADMIN: Delete user by identifier (soft delete)
     *
     * Soft deletes a user. The user data is retained for 30 days before GDPR anonymization.
     * Use restoreUser() to restore within the grace period.
     *
     * @param identifier - User unique identifier (id, email, externalId, accountAddress, etc.)
     * @returns Promise resolving to success status and message
     */
    async deleteUser(identifier) {
        return this.userApi.deleteUser(identifier);
    }
    /**
     * ADMIN: Restore deleted user by identifier
     *
     * Restores a soft-deleted user within the 30-day grace period.
     * After GDPR anonymization (30 days), restoration is not possible.
     *
     * @param identifier - User unique identifier (id, email, externalId, accountAddress, etc.)
     * @returns Promise resolving to restored user data
     */
    async restoreUser(identifier) {
        return this.userApi.restoreUser(identifier);
    }
}

export { UserApi, UserService };
//# sourceMappingURL=user.js.map
